import ReactDOM from "react-dom/client";
import {BrowserRouter} from "react-router-dom";
import {Amplify} from "aws-amplify";
import {CookieStorage} from "aws-amplify/utils";
import {cognitoUserPoolsTokenProvider} from "aws-amplify/auth/cognito";
import i18n from "i18next";
import {initReactI18next} from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import LocizeBackend from "i18next-locize-backend";
import ResourcesToBackend from "i18next-resources-to-backend";
import Backend from "i18next-chained-backend";
import {StyledEngineProvider} from "@mui/material/styles";
import {ThemeProvider} from "@mui/material";
import {App} from "./components/App";
import {appTheme} from "./theme";
import {flattenObject} from "./utils/i18nUtils";
import EN from "./assets/i18n/en.json";
import "./index.css";
import "jotai-devtools/styles.css";
import {initSentry} from "../tools/sentry";

initSentry();
const COGNITO_USERPOOL_ID = import.meta.env.VITE_APP_COGNITO_USERPOOL_ID as string;
const COGNITO_CLIENT_ID = import.meta.env.VITE_APP_COGNITO_CLIENT_ID as string;
const COGNITO_IDENTITY_POOL_ID = import.meta.env.VITE_APP_COGNITO_IDENTITY_POOL_ID as string;

const LOCIZE_PROJECT_ID = import.meta.env.VITE_LOCIZE_PROJECT_ID as string;

Amplify.configure({
    Auth: {
        Cognito: {
            userPoolId: COGNITO_USERPOOL_ID,
            userPoolClientId: COGNITO_CLIENT_ID,
            identityPoolId: COGNITO_IDENTITY_POOL_ID,
        },
    },
});

cognitoUserPoolsTokenProvider.setKeyValueStorage(
    new CookieStorage({
        domain: window.location.hostname,
        path: "/",
        secure: true,
    }),
);

const locizeOptions = {
    projectId: LOCIZE_PROJECT_ID,
    referenceLng: "en",
};

void i18n
    .use(Backend)
    .use(LanguageDetector)
    .use(initReactI18next) // passes i18n down to react-i18next
    .init({
        debug: false,
        defaultNS: "frontendweb",
        fallbackLng: "en",
        interpolation: {
            escapeValue: false, // react already safes from xss => https://www.i18next.com/translation-function/interpolation#unescape
        },
        react: {
            // https://react.i18next.com/latest/trans-component#trans-props
            transSupportBasicHtmlNodes: true,
        },
        backend: {
            backends: [LocizeBackend, ResourcesToBackend({en: {frontendweb: EN}})],
            backendOptions: [locizeOptions],
        },
        parseMissingKeyHandler(missingKey) {
            const value = flattenObject(EN)[missingKey];
            if (typeof value === "string") {
                return value;
            }
            return missingKey;
        },
    });

ReactDOM.createRoot(document.getElementById("root")!).render(
    <ThemeProvider theme={appTheme}>
        <StyledEngineProvider injectFirst>
            <BrowserRouter>
                <App />
            </BrowserRouter>
        </StyledEngineProvider>
    </ThemeProvider>,
);
